import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Container,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { colors } from '../styles';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: 'none',
  border: `1px solid ${alpha(colors.primary, 0.0625)}`,
}));

const DesignSystem = () => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  return (
    <Container maxWidth="lg" sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: colors.secondary }}>
        Design System Tokens & Components
      </Typography>

      {/* Colors Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Colors
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Paper sx={{ height: 60, backgroundColor: colors.primary, mb: 1, borderRadius: 1 }} />
                <Typography variant="caption">Primary</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{colors.primary}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Paper sx={{ height: 60, backgroundColor: colors.secondary, mb: 1, borderRadius: 1 }} />
                <Typography variant="caption">Secondary</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{colors.secondary}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Paper sx={{ height: 60, backgroundColor: colors.background, mb: 1, borderRadius: 1, border: `1px solid ${colors.secondary}20` }} />
                <Typography variant="caption">Background</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{colors.background}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Paper sx={{ height: 60, backgroundColor: colors.white, mb: 1, borderRadius: 1, border: `1px solid ${colors.secondary}20` }} />
                <Typography variant="caption">White</Typography>
                <Typography variant="caption" display="block" color="text.secondary">{colors.white}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Status Colors
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(colors.status).map(([key, value]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Box sx={{ textAlign: 'center' }}>
                  <Paper sx={{ height: 40, backgroundColor: value, mb: 1, borderRadius: 1 }} />
                  <Typography variant="caption">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">{value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Typography Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Typography
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h1" sx={{ fontWeight: 700, color: colors.primary }}>
                H1 - Heading 1
              </Typography>
              <Typography variant="body2" color="text.secondary">Font Weight: 700, Color: Primary</Typography>
            </Box>
            <Box>
              <Typography variant="h2" sx={{ fontWeight: 600, color: colors.secondary }}>
                H2 - Heading 2
              </Typography>
              <Typography variant="body2" color="text.secondary">Font Weight: 600, Color: Secondary</Typography>
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: colors.secondary }}>
                H3 - Heading 3
              </Typography>
              <Typography variant="body2" color="text.secondary">Font Weight: 600, Color: Secondary</Typography>
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: colors.secondary }}>
                H4 - Heading 4
              </Typography>
              <Typography variant="body2" color="text.secondary">Font Weight: 600, Color: Secondary</Typography>
            </Box>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Body 1 - Regular body text
              </Typography>
              <Typography variant="body2" color="text.secondary">Font Weight: 500, Regular text</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Body 2 - Secondary body text
              </Typography>
              <Typography variant="caption" color="text.secondary">Font Weight: 500, Smaller text</Typography>
            </Box>
          </Stack>
        </CardContent>
      </StyledCard>

      {/* Buttons Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Buttons
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button variant="contained">Contained</Button>
            <Button variant="outlined">Outlined</Button>
            <Button variant="text">Text</Button>
            <Button variant="contained" disabled>Disabled</Button>
          </Stack>
        </CardContent>
      </StyledCard>

      {/* Inputs Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Form Inputs
          </Typography>
          <Stack spacing={3}>
            <TextField label="Standard Text Field" variant="outlined" fullWidth />
            <TextField label="Email" type="email" variant="outlined" fullWidth />
            <TextField
              label="Error State"
              variant="outlined"
              error
              helperText="This field has an error"
              fullWidth
            />
            <TextField label="Disabled" variant="outlined" disabled fullWidth />
          </Stack>
        </CardContent>
      </StyledCard>

      {/* Chips Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Chips
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="Default" />
            <Chip label="Ready" color="success" />
            <Chip label="Syncing" color="warning" />
            <Chip label="Error" color="error" />
            <Chip label="Info" color="info" />
          </Stack>
        </CardContent>
      </StyledCard>

      {/* Alerts Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Alerts
          </Typography>
          <Stack spacing={2}>
            <Alert severity="success">Success message</Alert>
            <Alert severity="info">Info message</Alert>
            <Alert severity="warning">Warning message</Alert>
            <Alert severity="error">Error message</Alert>
          </Stack>
        </CardContent>
      </StyledCard>

      {/* Dialog Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Dialog
          </Typography>
          <Button variant="outlined" onClick={() => setDialogOpen(true)}>
            Open Dialog
          </Button>
          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            PaperProps={{ sx: { borderRadius: '20px' } }}
          >
            <DialogTitle>Sample Dialog</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This is a sample dialog to demonstrate the design system styling.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={() => setDialogOpen(false)}>OK</Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </StyledCard>

      {/* Spacing Section */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Spacing Scale
          </Typography>
          <Stack spacing={1}>
            {[
              { name: 'XS (4px)', height: '4px' },
              { name: 'SM (8px)', height: '8px' },
              { name: 'MD (16px)', height: '16px' },
              { name: 'LG (24px)', height: '24px' },
              { name: 'XL (32px)', height: '32px' },
            ].map((item) => (
              <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Paper sx={{ width: '100%', height: item.height, backgroundColor: colors.primary }} />
                <Typography variant="body2">{item.name}</Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </StyledCard>
    </Container>
  );
};

export default DesignSystem;
